/**
 * The forced set-password step of an ESS invite — the employee's first
 * sign-in. Shared by both invite channels:
 *
 *   - the emailed LINK (`invite_accept_screen.tsx`, credential `link`), and
 *   - the one-time CODE typed into the app (`invite_code_screen.tsx`,
 *     credential `code`).
 *
 * No session exists until this form succeeds. Two modes, from the lookup's
 * `account_exists`:
 *
 *   - **create_account** — choose a password, typed twice (+ name and ONE
 *     Terms-of-Service-and-Privacy consent box that sets both flags); the
 *     confirmation is checked client-side only. The
 *     server never sets or emails a password; this is where it is born.
 *   - **sign_in** — the address already has an account: prove its current
 *     password. It is never overwritten here.
 *
 * Submission goes through `submitInviteAccept` (`lib/auth/invite_accept_submit.ts`),
 * which also persists the native-shell session token before navigating —
 * the same order as the login screen. Every dependency is injectable so the
 * native path is testable without a Capacitor runtime.
 */
import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  Checkbox,
  FormField,
  Input,
  PasswordInput,
} from '@jumpstone/ui-react';
import { getPublicAccountTrpcClient } from '../../lib/auth/public_account_trpc.js';
import { performCookieLoginPostAction } from '../../lib/auth/auth_context.js';
import { clearStoredTokens } from '../../lib/auth/token_store.js';
import { isNative } from '../../lib/platform.js';
import { nativeClientInput } from '../../lib/auth/native_shell_auth.js';
import { setNativeSessionToken } from '../../lib/auth/native_session_store.js';
import type { AuthErrorDisplay } from '../../lib/auth/auth_error_display.js';
import {
  buildInviteAcceptRequest,
  hasInviteAcceptFieldErrors,
  resolveInviteAcceptMode,
  validateCreateAccountValues,
  validateSignInValues,
  type InviteAcceptFieldErrors,
  type InviteAcceptRequest,
  type InviteCredential,
} from '../../lib/auth/invite_accept_logic.js';
import {
  submitInviteAccept,
  type InviteAcceptSubmitDeps,
} from '../../lib/auth/invite_accept_submit.js';
import { AuthFormError } from './auth_form_error.js';

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; error: AuthErrorDisplay };

function submitViaPublicClient(
  request: InviteAcceptRequest,
): Promise<{ native_session_token?: string }> {
  const client = getPublicAccountTrpcClient();
  return request.procedure === 'acceptWithCredentials'
    ? client.essInviteSignup.acceptWithCredentials.mutate(request.input)
    : client.essInviteSignup.acceptWithCode.mutate(request.input);
}

function defaultDeps(native: boolean): InviteAcceptSubmitDeps {
  return {
    submit: submitViaPublicClient,
    afterLogin: () =>
      performCookieLoginPostAction({
        clearBearerTokens: () => clearStoredTokens(),
      }),
    native,
    setToken: (token) => setNativeSessionToken(token, { native }),
    navigate: (path) => {
      // A full navigation: `AuthProvider` boots once on mount, so remounting
      // the app is what re-reads the new cookie / stored native token. No
      // "Sign in with Ledger" handoff — see `invite_accept_screen.tsx`.
      window.location.assign(path);
    },
  };
}

export interface InviteSetPasswordFormProps {
  credential: InviteCredential;
  invitedEmail: string;
  companyName: string;
  accountExists: boolean;
  onAccepted?: () => void;
  /** Test seam — overrides the production transport/storage/navigation. */
  deps?: Partial<InviteAcceptSubmitDeps>;
}

export function InviteSetPasswordForm({
  credential,
  invitedEmail,
  companyName,
  accountExists,
  onAccepted,
  deps,
}: InviteSetPasswordFormProps): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [password, setPassword] = useState('');
  // Create-account mode only; client-side check, never sent to the API.
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  // ONE consent box for both documents, same as the employer sign-up screen.
  // It still sets BOTH `accept_tos` and `accept_privacy`, so the server records
  // each document's accepted version separately (GDPR Art. 7).
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<InviteAcceptFieldErrors>({});
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' });

  const mode = resolveInviteAcceptMode(accountExists);
  const creating = mode === 'create_account';
  const submitting = submitState.kind === 'submitting';

  async function handleSubmit(): Promise<void> {
    const errors = creating
      ? validateCreateAccountValues({
          password,
          confirm_password: confirmPassword,
          full_name: fullName,
          accept_tos: acceptTerms,
          accept_privacy: acceptTerms,
        })
      : validateSignInValues({ password });
    setFieldErrors(errors);
    if (hasInviteAcceptFieldErrors(errors)) return;

    const native = deps?.native ?? isNative();
    const resolved: InviteAcceptSubmitDeps = {
      ...defaultDeps(native),
      ...deps,
      native,
      onAccepted,
    };
    setSubmitState({ kind: 'submitting' });
    const outcome = await submitInviteAccept(
      buildInviteAcceptRequest({
        credential,
        invitedEmail,
        mode,
        values: {
          password,
          full_name: fullName,
          accept_tos: acceptTerms,
          accept_privacy: acceptTerms,
        },
        client: nativeClientInput(native),
      }),
      resolved,
    );
    if (outcome.kind === 'error') {
      setSubmitState({ kind: 'error', error: outcome.error });
    }
  }

  return (
    <>
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-[color:var(--ink)]">
          {t('invite_accept.title', { company: companyName })}
        </h1>
        <p className="mt-1 text-sm text-[color:var(--ink-muted)]">
          {creating
            ? t('invite_accept.dek_create_account')
            : t('invite_accept.dek_sign_in')}
        </p>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
        className="flex flex-col gap-4"
      >
        <FormField label={t('invite_accept.email_label')}>
          <Input value={invitedEmail} disabled readOnly />
        </FormField>

        {creating && (
          <FormField
            label={t('invite_accept.full_name_label')}
            htmlFor="invite-full-name"
            error={fieldErrors.full_name && t('invite_accept.full_name_error')}
          >
            <Input
              id="invite-full-name"
              autoComplete="name"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              disabled={submitting}
            />
          </FormField>
        )}

        <FormField
          label={
            creating
              ? t('invite_accept.new_password_label')
              : t('invite_accept.password_label')
          }
          htmlFor="invite-password"
          helpText={creating ? t('invite_accept.new_password_hint') : undefined}
          error={fieldErrors.password && t('invite_accept.password_error')}
        >
          <PasswordInput
            id="invite-password"
            autoComplete={creating ? 'new-password' : 'current-password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={submitting}
            labels={{
              show_password: t('invite_accept.show_password'),
              hide_password: t('invite_accept.hide_password'),
            }}
          />
        </FormField>

        {creating && (
          <FormField
            label={t('auth.confirm_password_label')}
            htmlFor="invite-confirm-password"
            error={
              fieldErrors.confirm_password && t('auth.confirm_password_error')
            }
          >
            <PasswordInput
              id="invite-confirm-password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              disabled={submitting}
              labels={{
                show_password: t('invite_accept.show_password'),
                hide_password: t('invite_accept.hide_password'),
              }}
            />
          </FormField>
        )}

        {creating && (
          <>
            <label className="flex items-start gap-2 text-sm text-[color:var(--ink)]">
              <Checkbox
                checked={acceptTerms}
                onCheckedChange={setAcceptTerms}
                disabled={submitting}
              />
              <span>{t('auth.accept_terms')}</span>
            </label>
            {(fieldErrors.accept_tos || fieldErrors.accept_privacy) && (
              <p
                role="alert"
                className="text-[13px] text-[var(--color-danger)]"
              >
                {t('auth.accept_terms_error')}
              </p>
            )}
          </>
        )}

        {submitState.kind === 'error' && (
          <AuthFormError error={submitState.error} />
        )}

        <Button
          type="submit"
          variant="primary"
          loading={submitting}
          className="mt-2 w-full justify-center"
        >
          {submitting
            ? t('invite_accept.submitting')
            : creating
              ? t('invite_accept.submit_create_account')
              : t('invite_accept.submit_sign_in')}
        </Button>
      </form>
    </>
  );
}
